import React, { useEffect, useState } from 'react';
import { MapPin, Package, RefreshCw } from 'lucide-react';
import apiClient from '../utils/api';

interface PricingRow {
  name: string;
  state: string;
  deliveryFee: number;
  returnFee: number;
}

const PROVINCE_ORDER = ['Kigali City', 'Eastern Province', 'Southern Province', 'Western Province', 'Northern Province'];

export const PricingPage: React.FC = () => {
  const [rows, setRows] = useState<PricingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    apiClient.getPublicPricing()
      .then(res => setRows(res.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = rows.filter(r =>
    r.name.toLowerCase().includes(search.toLowerCase()) ||
    r.state.toLowerCase().includes(search.toLowerCase())
  );

  // Group by province
  const grouped = PROVINCE_ORDER.reduce<Record<string, PricingRow[]>>((acc, province) => {
    const items = filtered.filter(r => r.state === province);
    if (items.length) acc[province] = items;
    return acc;
  }, {});

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 20px', fontFamily: 'system-ui, sans-serif' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        <img src="/logo.png" alt="E.H.C Logistics" style={{ height: 48, marginBottom: 16 }} />
        <h1 style={{ fontSize: 28, fontWeight: 700, color: '#1e293b', margin: '0 0 8px' }}>
          Delivery Rate Card
        </h1>
        <p style={{ color: '#64748b', fontSize: 15, margin: 0 }}>
          Rwanda-wide delivery — all prices in RWF, base rate for 0–2 kg packages
        </p>
      </div>

      {/* Info banner */}
      <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '14px 20px', marginBottom: 28, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <Package size={18} color="#3b82f6" style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 13, color: '#1e40af', lineHeight: 1.6 }}>
          <strong>Base rate applies to packages 0–2 kg.</strong> Heavier packages may incur additional charges.
          The return fee applies when a package cannot be delivered and must be returned to the sender.
          Contact us for bulk or express pricing.
        </div>
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 24 }}>
        <MapPin size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
        <input
          placeholder="Search district or province..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            width: '100%', paddingLeft: 36, padding: '10px 12px 10px 36px',
            border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14,
            outline: 'none', boxSizing: 'border-box',
          }}
        />
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
          <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
          <div style={{ marginTop: 12 }}>Loading rates...</div>
        </div>
      ) : (
        Object.entries(grouped).map(([province, districts]) => (
          <div key={province} style={{ marginBottom: 32 }}>
            <div style={{
              fontSize: 12, fontWeight: 700, color: '#6366f1', textTransform: 'uppercase',
              letterSpacing: '0.08em', marginBottom: 10, paddingBottom: 6,
              borderBottom: '2px solid #e0e7ff',
            }}>
              {province}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={th}>#</th>
                  <th style={th}>Route</th>
                  <th style={{ ...th, textAlign: 'right' }}>Delivery Fee</th>
                  <th style={{ ...th, textAlign: 'right' }}>Return Fee</th>
                </tr>
              </thead>
              <tbody>
                {districts.map((row, idx) => (
                  <tr key={row.name} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ ...td, color: '#94a3b8', width: 40 }}>{idx + 1}</td>
                    <td style={{ ...td, fontWeight: 500 }}>
                      {province === 'Kigali City' ? row.name : `Kigali → ${row.name}`}
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#166534' }}>
                      {row.deliveryFee.toLocaleString()} RWF
                    </td>
                    <td style={{ ...td, textAlign: 'right', color: '#64748b' }}>
                      {row.returnFee.toLocaleString()} RWF
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}

      {!loading && filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
          No districts found for "{search}"
        </div>
      )}

      <div style={{ textAlign: 'center', marginTop: 40, fontSize: 12, color: '#94a3b8' }}>
        E.H.C Logistics Platform · Rwanda · All prices in RWF
      </div>
    </div>
  );
};

const th: React.CSSProperties = {
  padding: '8px 12px', textAlign: 'left', fontSize: 12,
  fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em',
};
const td: React.CSSProperties = {
  padding: '10px 12px', fontSize: 14, color: '#1e293b',
};
